import {
  Blocks,
  CheckCircle2,
  LoaderCircle,
  Radar,
} from 'lucide-react';
import type { PaymentConfirmation } from '@/api/types';
import {
  getNetworkConfirmations,
  getNetworkLabel,
  getTokenLabel,
} from '@/common/consts';

type PaymentConfirmationStatusProps = {
  confirmation?: PaymentConfirmation | null;
  fallbackNetwork?: string;
  fallbackAsset?: string;
  className?: string;
};

function formatDateTime(value?: string | null) {
  if (!value) {
    return 'Not available';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return 'Not available';
  }

  return new Intl.DateTimeFormat('en-US', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}

function formatTransactionHash(txHash: string) {
  const normalized = txHash.trim();
  const prefixLength = normalized.startsWith('0x') ? 10 : 8;
  const suffixLength = 6;

  if (normalized.length <= prefixLength + suffixLength + 3) {
    return normalized;
  }

  return `${normalized.slice(0, prefixLength)}...${normalized.slice(-suffixLength)}`;
}

function resolvePresentation(confirmation?: PaymentConfirmation | null) {
  switch (confirmation?.state) {
    case 'CONFIRMED':
      return {
        title: 'Confirmed on-chain',
        message:
          'Your transfer reached the required number of confirmations and is now being credited to the invoice.',
        icon: CheckCircle2,
        iconClassName: 'bg-emerald-500/15 text-emerald-300',
        badgeClassName:
          'border-emerald-700/50 bg-emerald-900/30 text-emerald-200',
        progressClassName: 'bg-emerald-400',
      };
    case 'CONFIRMING':
      return {
        title: 'Confirming on-chain',
        message:
          'We detected your transfer and are waiting for the remaining block confirmations before crediting the invoice.',
        icon: LoaderCircle,
        iconClassName: 'bg-sky-500/15 text-sky-300',
        badgeClassName: 'border-sky-700/50 bg-sky-900/30 text-sky-200',
        progressClassName: 'bg-sky-400',
      };
    case 'DETECTED':
      return {
        title: 'Transaction detected',
        message:
          'Your transfer is included on-chain. We will start crediting it after the required confirmation threshold is reached.',
        icon: Radar,
        iconClassName: 'bg-orange-500/15 text-orange-200',
        badgeClassName:
          'border-orange-700/50 bg-orange-900/30 text-orange-100',
        progressClassName: 'bg-orange-400',
      };
    default:
      return {
        title: 'Waiting for transaction',
        message:
          'As soon as we detect your transfer on-chain, the confirmation progress will appear here.',
        icon: Blocks,
        iconClassName: 'bg-white/10 text-gray-300',
        badgeClassName: 'border-[#2a2b32] bg-[#111215] text-gray-200',
        progressClassName: 'bg-[#e97724]',
      };
  }
}

export function PaymentConfirmationStatus({
  confirmation,
  fallbackNetwork,
  fallbackAsset,
  className = '',
}: PaymentConfirmationStatusProps) {
  const network = confirmation?.network ?? fallbackNetwork;
  const asset = confirmation?.asset ?? fallbackAsset;
  const requiredConfirmations =
    confirmation?.requiredConfirmations ??
    (network ? getNetworkConfirmations(network) : undefined);

  if (!confirmation && !network) {
    return null;
  }

  const presentation = resolvePresentation(confirmation);
  const Icon = presentation.icon;
  const confirmations = confirmation?.confirmations ?? 0;
  const progressPercent =
    requiredConfirmations && requiredConfirmations > 0
      ? Math.min((confirmations / requiredConfirmations) * 100, 100)
      : 0;

  return (
    <section
      className={`rounded-2xl border ${presentation.badgeClassName} p-5 shadow-sm ${className}`.trim()}
    >
      <div className="flex items-start gap-3">
        <div
          className={`mt-0.5 rounded-full p-2 ${presentation.iconClassName}`}
        >
          <Icon
            className={`h-5 w-5 ${
              confirmation?.state === 'CONFIRMING' ? 'animate-spin' : ''
            }`}
          />
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider opacity-80">
                Blockchain confirmation
              </p>
              <h3 className="mt-1 text-base font-semibold text-white">
                {presentation.title}
              </h3>
            </div>

            {requiredConfirmations ? (
              <div className="rounded-full border border-white/10 bg-black/15 px-3 py-1 text-xs font-medium text-white/90">
                {confirmations} / {requiredConfirmations} blocks
              </div>
            ) : null}
          </div>

          <p className="mt-2 text-sm leading-6 text-white/80">
            {presentation.message}
          </p>

          {requiredConfirmations ? (
            <div className="mt-4">
              <div className="h-2 overflow-hidden rounded-full bg-black/25">
                <div
                  className={`h-full rounded-full transition-[width] duration-500 ${presentation.progressClassName}`}
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
            </div>
          ) : null}

          <div className="mt-4 grid gap-3 text-sm text-white/85 sm:grid-cols-2">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-white/45">
                Network
              </p>
              <p className="mt-1">{network ? getNetworkLabel(network) : 'Not available'}</p>
            </div>

            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-white/45">
                Asset
              </p>
              <p className="mt-1">
                {asset ? getTokenLabel(asset) : 'Not available'}
                {confirmation?.amount ? ` • ${confirmation.amount}` : ''}
              </p>
            </div>

            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-white/45">
                Detected at
              </p>
              <p className="mt-1">{formatDateTime(confirmation?.detectedAt)}</p>
            </div>

            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-white/45">
                Transaction hash
              </p>
              <p
                className="mt-1 truncate font-mono text-xs text-white/85"
                title={confirmation?.txHash ?? 'Not available'}
              >
                {confirmation?.txHash
                  ? formatTransactionHash(confirmation.txHash)
                  : 'Not available'}
              </p>
            </div>

            {confirmation?.confirmedAt ? (
              <div className="sm:col-span-2">
                <p className="text-[10px] font-bold uppercase tracking-wider text-white/45">
                  Confirmed at
                </p>
                <p className="mt-1">{formatDateTime(confirmation.confirmedAt)}</p>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
}
